import {
  type Audience,
  POLL_AUDIENCES,
  type Poll,
  addDays,
  isPollOpen,
  shortDate,
  todayIso,
  useClasses,
  useCurrentContext,
  usePollActions,
  usePollResults,
  usePolls,
} from '@shkola/api';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ErrorText, Field, Modal, Select, Spinner } from '../components/ui';
import {
  Block,
  DEEP,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  grad,
  pillClass,
} from '../design/forms';
import { ChartBarIcon, CheckIcon, PaperPlaneTiltIcon, PlusIcon } from '../design/icons';
import { useMyMemberIds } from './AnnouncementsPage';

/*
 * «Опитування» in «Форми 2» (28.10): the title on the paper; every poll a white block — who is asked and
 * till when as soft labels, the question big and light, the answers as rows on the paper to tick (the
 * chosen one darker, its circle filled), the dark pill to send; after the answer — the results as soft
 * bars in lavender with mine ticked. The author and the administration close and remove a poll.
 */

const FIELD =
  'h-11 w-full rounded-[14px] bg-white px-3.5 text-[15px] text-[var(--sh-ink)] outline-none ring-1 ring-[#E4E0D7] ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]';

/** A label: a soft rectangle in a gradient, never a pill. */
function Tag({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

function NewPoll({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const classes = useClasses(ctx.schoolId);
  const actions = usePollActions(ctx.schoolId);
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  const audiences: ReadonlyArray<Audience> =
    ctx.kind === 'admin' ? POLL_AUDIENCES : ['class_parents'];
  const [audience, setAudience] = useState<Audience>(audiences[0] as Audience);
  const [classId, setClassId] = useState(homeroom?.id ?? '');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [closesOn, setClosesOn] = useState(addDays(todayIso(), 7));
  const [error, setError] = useState<string>();
  const klass = homeroom?.id ?? (classId || classes.data?.[0]?.id);
  const filled = options.filter((o) => o.trim()).length;

  return (
    <Modal title={t('polls.new')} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(undefined);
          actions.create
            .mutateAsync({
              audience,
              classId: audience === 'class_parents' ? (klass ?? null) : null,
              question,
              options,
              multiple,
              // until the end of the chosen day (the browser's clock is the school's)
              closesAt: closesOn ? new Date(`${closesOn}T23:59:00`).toISOString() : null,
            })
            .then(onClose, () => setError(t('common.error')));
        }}
      >
        <div className="flex flex-wrap gap-2">
          <Select
            label={t('polls.audience')}
            value={audience}
            onChange={(e) => setAudience(e.target.value as Audience)}
          >
            {audiences.map((a) => (
              <option key={a} value={a}>
                {t(`announcements.audiences.${a}`)}
              </option>
            ))}
          </Select>
          {audience === 'class_parents' && !homeroom && (
            <Select
              aria-label={t('polls.audience')}
              value={klass ?? ''}
              onChange={(e) => setClassId(e.target.value)}
            >
              {(classes.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
        </div>
        <p className="text-[13px]" style={{ color: INK_SOFT }}>
          {t('polls.onlyAdults')}
        </p>
        <Field
          id="poll-question"
          label={t('polls.question')}
          required
          maxLength={300}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-[13px] font-medium" style={{ color: INK_SOFT }}>
            {t('polls.options')}
          </legend>
          {options.map((o, i) => (
            <input
              // options have no id before saving; the list only grows at the end
              key={i}
              aria-label={t('polls.option', { n: i + 1 })}
              placeholder={t('polls.option', { n: i + 1 })}
              maxLength={200}
              value={o}
              onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))}
              className={FIELD}
            />
          ))}
          {options.length < 10 && (
            <div>
              <button
                type="button"
                className={pillClass('light', 'sm')}
                onClick={() => setOptions([...options, ''])}
              >
                {t('polls.addOption').replace(/^\+\s*/, '')}
                <PillIcon icon={PlusIcon} size="sm" />
              </button>
            </div>
          )}
        </fieldset>
        <label className="flex items-center gap-2.5 text-[14px]" style={{ color: INK }}>
          <input
            type="checkbox"
            className="size-4 accent-[var(--sh-dark)]"
            checked={multiple}
            onChange={(e) => setMultiple(e.target.checked)}
          />
          {t('polls.multiple')}
        </label>
        <Field
          id="poll-closes"
          type="date"
          label={t('polls.closesAt')}
          min={addDays(todayIso(), 1)}
          value={closesOn}
          onChange={(e) => setClosesOn(e.target.value)}
        />
        <p className="text-[13px]" style={{ color: INK_SOFT }}>
          {t('polls.anonymous')}
        </p>
        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={actions.create.isPending || !question.trim() || filled < 2}>
          {t('polls.new')}
        </Button>
      </form>
    </Modal>
  );
}

function PollCard({ poll, mine }: { poll: Poll; mine: ReadonlyArray<string> }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = usePollActions(ctx.schoolId);
  const classes = useClasses(ctx.schoolId);
  const open = isPollOpen(poll);
  const staff = ctx.kind === 'admin' || ctx.kind === 'teacher';
  const author = poll.authorMemberId !== null && mine.includes(poll.authorMemberId);
  const manages = author || ctx.kind === 'admin';
  const answered = poll.mine.length > 0;
  const mayAnswer = open && (poll.audience === 'staff' ? staff : !staff);
  const showResults = answered || !open || manages;
  const results = usePollResults(poll.id, showResults);
  const [choice, setChoice] = useState<string[]>(poll.mine);
  const [editing, setEditing] = useState(!answered);
  const className = classes.data?.find((c) => c.id === poll.classId)?.label;
  const top = results.data
    ? Math.max(0, ...poll.options.map((o) => results.data.votes[o.id] ?? 0))
    : 0;

  return (
    <Block>
      <div className="flex flex-wrap items-center gap-1.5">
        <Tag tone="lavender">
          {t(`announcements.audiences.${poll.audience}`)}
          {className && ` · ${className}`}
        </Tag>
        {open ? (
          <Tag tone="mint">
            {t('polls.open')}
            {poll.closesAt &&
              ` ${t('polls.openUntil', { date: shortDate(poll.closesAt.slice(0, 10)) })}`}
          </Tag>
        ) : (
          <Tag tone="stone">{t('polls.closed')}</Tag>
        )}
        {poll.multiple && <Tag tone="stone">{t('polls.multiple')}</Tag>}
        {answered && (
          <Tag tone="sky">
            <CheckIcon size={12} weight="bold" aria-hidden />
            {t('polls.answered')}
          </Tag>
        )}
      </div>
      <h2
        className="mt-3 mb-4 font-display text-[24px] leading-[30px] font-light tracking-[-0.025em]"
        style={{ color: INK }}
      >
        {poll.question}
      </h2>

      {mayAnswer && editing ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            actions.vote.mutate(
              { pollId: poll.id, options: choice },
              { onSuccess: () => setEditing(false) },
            );
          }}
        >
          {poll.options.map((o) => {
            const on = choice.includes(o.id);
            return (
              <label
                key={o.id}
                className="flex cursor-pointer items-center gap-3 rounded-[16px] px-4 py-3 text-[15px] transition-colors focus-within:ring-2 focus-within:ring-[var(--sh-dark)] hover:bg-[#EFECE5]"
                style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
              >
                <input
                  type={poll.multiple ? 'checkbox' : 'radio'}
                  name={`poll-${poll.id}`}
                  className="sr-only"
                  checked={on}
                  onChange={(e) =>
                    setChoice(
                      poll.multiple
                        ? e.target.checked
                          ? [...choice, o.id]
                          : choice.filter((x) => x !== o.id)
                        : [o.id],
                    )
                  }
                />
                <span
                  aria-hidden
                  className={`flex size-6 shrink-0 items-center justify-center ${poll.multiple ? 'rounded-[7px]' : 'rounded-full'}`}
                  style={
                    on
                      ? { backgroundColor: 'var(--sh-dark)', color: '#FFFFFF' }
                      : { boxShadow: 'inset 0 0 0 1.5px var(--sh-dark)' }
                  }
                >
                  {on && <CheckIcon size={13} weight="bold" />}
                </span>
                <span className={on ? 'font-medium' : ''}>{o.label}</span>
              </label>
            );
          })}
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={choice.length === 0 || actions.vote.isPending}
              className={`${pillClass('dark')} disabled:opacity-35`}
            >
              {t('polls.send')}
              <PillIcon icon={PaperPlaneTiltIcon} />
            </button>
            {!answered && (
              <span className="text-[13px]" style={{ color: INK_SOFT }}>
                {t('polls.resultsAfter')}
              </span>
            )}
          </div>
        </form>
      ) : !showResults ? (
        // asked of others and still open: what is asked, without the counts
        <ul className="flex flex-col gap-2">
          {poll.options.map((o) => (
            <li
              key={o.id}
              className="rounded-[16px] px-4 py-3 text-[15px]"
              style={{ backgroundColor: PAPER, color: INK_SOFT }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      ) : (
        results.data && (
          <div className="flex flex-col gap-3">
            {poll.options.map((o) => {
              const votes = results.data.votes[o.id] ?? 0;
              const share = results.data.answered
                ? Math.round((votes / results.data.answered) * 100)
                : 0;
              const picked = poll.mine.includes(o.id);
              const lead = votes > 0 && votes === top;
              return (
                <div key={o.id}>
                  <div className="flex items-baseline justify-between gap-3 text-[15px]">
                    <span
                      className={`flex min-w-0 items-center gap-1.5 ${picked ? 'font-medium' : ''}`}
                      style={{ color: INK }}
                    >
                      {picked && (
                        <CheckIcon
                          size={14}
                          weight="bold"
                          aria-hidden
                          className="shrink-0"
                          color={GRAD.lavender.ink}
                        />
                      )}
                      {o.label}
                    </span>
                    <span className="shrink-0 tabular-nums" style={{ color: INK_SOFT }}>
                      {votes} · <span style={{ color: INK }}>{share}%</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-2.5 rounded-full" style={{ backgroundColor: PAPER }}>
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${share}%`,
                        backgroundImage: lead
                          ? `linear-gradient(90deg, ${DEEP.lavender[0]} 0%, ${DEEP.lavender[1]} 100%)`
                          : `linear-gradient(90deg, ${GRAD.lavender.to} 0%, ${DEEP.lavender[0]} 100%)`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
            <p className="pt-1 text-[13px]" style={{ color: INK_FAINT }}>
              {t('polls.stats', {
                answered: results.data.answered,
                audience: results.data.audience,
              })}
            </p>
          </div>
        )
      )}

      {((mayAnswer && answered && !editing) || manages) && (
        <div className="mt-5 flex flex-wrap gap-2">
          {mayAnswer && answered && !editing && (
            <button
              type="button"
              className={`${pillClass('light', 'sm')} pr-3.5`}
              onClick={() => setEditing(true)}
            >
              {t('polls.change')}
            </button>
          )}
          {manages && open && (
            <button
              type="button"
              className={`${pillClass('light', 'sm')} pr-3.5 disabled:opacity-50`}
              disabled={actions.close.isPending}
              onClick={() => actions.close.mutate({ pollId: poll.id })}
            >
              {t('polls.close')}
            </button>
          )}
          {manages && (
            <button
              type="button"
              className={`${pillClass('light', 'sm')} pr-3.5 disabled:opacity-50`}
              disabled={actions.close.isPending}
              onClick={() => actions.close.mutate({ pollId: poll.id, remove: true })}
            >
              {t('polls.remove')}
            </button>
          )}
        </div>
      )}
    </Block>
  );
}

export function PollsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const polls = usePolls(ctx.schoolId);
  const mine = useMyMemberIds(ctx.schoolId);
  const [creating, setCreating] = useState(false);
  const canCreate = ctx.kind === 'admin' || (ctx.kind === 'teacher' && ctx.homeroomClass);
  const all = polls.data ?? [];
  // the open ones first: those can still be answered
  const sorted = [...all.filter((p) => isPollOpen(p)), ...all.filter((p) => !isPollOpen(p))];
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('polls.title')}>
        {canCreate && (
          <button
            type="button"
            className={`${pillClass('dark')} max-lg:w-11 max-lg:justify-center max-lg:px-0`}
            onClick={() => setCreating(true)}
          >
            <span className="max-lg:sr-only">{t('polls.new')}</span>
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </PageHead>
      {polls.isPending ? (
        <Block>
          <Spinner />
        </Block>
      ) : all.length === 0 ? (
        <Block className="items-center py-14 text-center">
          <span
            className="flex size-20 items-center justify-center rounded-full"
            style={{ backgroundImage: grad('aqua'), color: GRAD.aqua.ink }}
          >
            <ChartBarIcon size={34} weight="fill" aria-hidden />
          </span>
          <p className="mt-4 font-display text-[24px] font-light" style={{ color: INK }}>
            {t('polls.empty')}
          </p>
        </Block>
      ) : (
        <div className="gap-4 xl:columns-2 [&>*]:mb-4 [&>*]:break-inside-avoid">
          {sorted.map((p) => (
            <PollCard key={p.id} poll={p} mine={mine} />
          ))}
        </div>
      )}
      {creating && <NewPoll onClose={() => setCreating(false)} />}
    </div>
  );
}
